import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { PublishJobDrawer } from '@/components/play/PublishJobDrawer';
import { PublishTemplateEditor } from '@/components/play/PublishTemplateEditor';
import { describeTemplate } from '@/lib/play-template';
import { PublishAccountEditor } from '@/components/play/PublishAccountEditor';
import { listAccounts, createAccount } from '@/api/play';
import { getApps } from '@/api/apps';
import { getBuilds, getBuildConfig, getBuildRepos, saveBuildConfig } from '@/api/app-builds';
import { getStoreAssets, getStoreListings, storeAssetUrl } from '@/api/app-handoff';
import { AppBuildPanel } from './apps-setup/BuildTab';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { listLiveSessions, openLiveSession } from '@/api/playLive';
import {
  listJobs, createJob, listTemplates, deleteTemplate, listPublishAccounts, onboardAccount, markSaInvited,
  type PublishJob, type PublishTemplate, type PublishAccount,
} from '@/api/playPublish';

const fmtUnix = (s: number) => new Date(s * 1000).toLocaleString();

const inputCls = 'w-full px-3 py-2 rounded-md bg-background border border-border text-content text-sm';

const SESSION_CLS: Record<string, string> = {
  ok: 'bg-green-500/15 text-green-400',
  expired: 'bg-amber-500/15 text-amber-400',
  locked: 'bg-red-500/15 text-red-400',
  unknown: 'bg-surface-muted text-content-muted',
};

// How long ago the console was last opened in this profile. Anything past 2.5
// days is what the runner treats as stale and refreshes before it publishes.
function keepaliveAge(ts: number | null): string {
  if (!ts) return 'never';
  const hours = Math.floor((Date.now() / 1000 - ts) / 3600);
  if (hours < 1) return 'just now';
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const JOB_STATUS_CLS: Record<string, string> = {
  done: 'bg-green-500/15 text-green-400',
  running: 'bg-blue-500/15 text-blue-400',
  pending: 'bg-surface-muted text-content-muted',
  blocked: 'bg-amber-500/15 text-amber-400',
  failed: 'bg-red-500/15 text-red-400',
};

const emptyForm = {
  account_id: '', template_id: '', package_name: '', title: '',
  default_lang: 'en-US', short_desc: '', full_desc: '',
  app_id: '', app_build_id: '', build_mode: 'new' as 'new' | 'existing' | 'none',
};

export function PlayPublish() {
  const qc = useQueryClient();
  const [openJobId, setOpenJobId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplEditing, setTplEditing] = useState<PublishTemplate | null>(null);
  const [tplError, setTplError] = useState<string | null>(null);
  const [accEditing, setAccEditing] = useState<PublishAccount | null>(null);
  const [accNew, setAccNew] = useState<string | null>(null); // name being typed; null = closed
  const [accNewError, setAccNewError] = useState<string | null>(null);
  const [buildOpen, setBuildOpen] = useState(false);
  const navigate = useNavigate();

  const { data: jobs = [], isLoading: jobsLoading, error: jobsError } = useQuery({
    queryKey: ['publish-jobs'],
    queryFn: listJobs,
    // Cheap enough at ~15 publications a week, and keeps the list honest while
    // the cron works through the API steps.
    refetchInterval: 15_000,
  });
  const { data: templates = [], isLoading: tplLoading } = useQuery({
    queryKey: ['publish-templates'],
    queryFn: listTemplates,
  });

  // An account starts as a name; the Google login, proxy and (later) the API
  // key are added on top. Opens the console-access editor right away.
  const addAccMut = useMutation({
    mutationFn: (name: string) => createAccount({ name }),
    onSuccess: async ({ id }, name) => {
      await qc.invalidateQueries({ queryKey: ['play-accounts'] });
      const list = await qc.fetchQuery({ queryKey: ['publish-accounts'], queryFn: listPublishAccounts });
      setAccNew(null);
      setAccEditing(list.find((a) => a.id === id) ?? {
        id, name, enabled: 1, google_email: null, iarc_email: null, contact_email: null, shardx_profile_id: null,
        session_status: 'unknown', last_keepalive_at: null, daily_publish_cap: 2,
        sa_client_email: null, onboard_requested_at: null, onboard_started_at: null, onboard_project_id: null, onboard_error: null,
        has_session_evidence: false, has_password: false, has_totp: false, backup_codes_left: 0,
        recovery_email: '', proxy_label: '', cookies_pending: false, platform: '',
        browser_profile_id: null, browser_profile_name: null, developer_id: null, developer_access: null, sa_invited_at: null,
      });
    },
    onError: (e: any) => setAccNewError(e?.message ?? 'Failed to add account'),
  });
  const { data: accounts = [] } = useQuery({ queryKey: ['play-accounts'], queryFn: listAccounts });
  // Only needed while the dialog is open: the app to build from, and its
  // finished AABs for the "use an existing one" case.
  const { data: apps = [] } = useQuery({ queryKey: ['apps'], queryFn: getApps, enabled: newOpen });
  // What the selected app brings to the listing: its texts (prefilled below),
  // its images, and the build config the factory will use.
  const listingsQuery = useQuery({
    queryKey: ['store-listings', form.app_id], queryFn: () => getStoreListings(form.app_id), enabled: newOpen && !!form.app_id,
  });
  const assetsQuery = useQuery({
    queryKey: ['store-assets', form.app_id], queryFn: () => getStoreAssets(form.app_id), enabled: newOpen && !!form.app_id,
  });
  const buildConfigQuery = useQuery({
    queryKey: ['build-config', form.app_id], queryFn: () => getBuildConfig(form.app_id), enabled: newOpen && !!form.app_id,
  });
  const reposQuery = useQuery({ queryKey: ['build-repos'], queryFn: getBuildRepos, enabled: newOpen && !!form.app_id });
  const repoMut = useMutation({
    mutationFn: ({ appId, repoId }: { appId: string; repoId: number }) => saveBuildConfig(appId, { base_repo_id: repoId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['build-config', form.app_id] }),
  });
  useEffect(() => {
    const src = listingsQuery.data?.find((l) => l.is_source) ?? listingsQuery.data?.[0];
    if (!src) return;
    setForm((f) => ({
      ...f,
      title: f.title || src.title,
      short_desc: f.short_desc || src.short_description,
      full_desc: f.full_desc || src.full_description,
    }));
  }, [listingsQuery.data]);
  const selectedApp = apps.find((a) => a.id === form.app_id);
  const shotsByLang = (assetsQuery.data ?? []).filter((a) => a.kind === 'screenshot').reduce<Record<string, typeof assetsQuery.data>>((m, a) => {
    (m[a.lang] ??= []).push(a); return m;
  }, {});
  const featureAsset = (assetsQuery.data ?? []).find((a) => a.kind === 'feature');
  const defaultShots = shotsByLang[form.default_lang] ?? [];
  const readiness = buildConfigQuery.data?.readiness;
  const buildBlocked = form.build_mode === 'new' && !!form.app_id && !!readiness && !readiness.ok;
  const { data: appBuilds = [] } = useQuery({
    queryKey: ['app-builds', form.app_id],
    queryFn: () => getBuilds(form.app_id),
    enabled: newOpen && !!form.app_id && form.build_mode === 'existing',
  });
  const invitedMut = useMutation({
    mutationFn: (v: { id: number; invited: boolean }) => markSaInvited(v.id, v.invited),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['publish-accounts'] }),
    onError: (e: any) => setLiveError(e?.message ?? 'Could not save'),
  });
  const onboardMut = useMutation({
    mutationFn: (id: number) => onboardAccount(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['publish-accounts'] }),
  });
  const { data: consoleAccounts = [], isLoading: accLoading } = useQuery({
    queryKey: ['publish-accounts'],
    queryFn: listPublishAccounts,
  });
  // Live browsers: a super admin or the `browser` addon can open an account's
  // profile and click through it from here (pages/play/LiveBrowser.tsx).
  const canLive = useAuthStore((st) => st.hasSection('browser'));
  const { data: liveSessions = [] } = useQuery({
    queryKey: ['live-sessions'],
    queryFn: listLiveSessions,
    enabled: canLive,
    refetchInterval: 10_000,
  });
  const liveByAccount = new Map(liveSessions.filter((l) => l.status !== 'closed' && l.account_id).map((l) => [l.account_id!, l]));
  const [liveError, setLiveError] = useState<string | null>(null);
  const openLiveMut = useMutation({
    mutationFn: (accountId: number) => openLiveSession(accountId),
    onSuccess: (sess) => navigate(`/play/live/${sess.id}`),
    onError: (e: any) => setLiveError(e?.message ?? 'Could not open the browser'),
  });

  const createMut = useMutation({
    mutationFn: () => createJob({
      account_id: Number(form.account_id),
      template_id: Number(form.template_id),
      package_name: form.package_name.trim(),
      title: form.title.trim(),
      default_lang: form.default_lang.trim() || 'en-US',
      short_desc: form.short_desc,
      full_desc: form.full_desc,
      ...(form.app_id ? { app_id: form.app_id } : {}),
      ...(form.build_mode === 'new' ? { build_mode: 'new' as const } : {}),
      ...(form.build_mode === 'existing' && form.app_build_id ? { app_build_id: form.app_build_id } : {}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['publish-jobs'] });
      setNewOpen(false);
      setForm(emptyForm);
    },
    onError: (e: any) => setFormError(e?.message ?? 'Failed to create publication'),
  });

  const deleteTplMut = useMutation({
    mutationFn: (id: number) => deleteTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['publish-templates'] }),
    onError: (e: any) => setTplError(e?.message ?? 'Failed to delete template'),
  });

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-content">First publication</h1>
        <Button onClick={() => { setForm(emptyForm); setFormError(null); setNewOpen(true); }}>
          New publication
        </Button>
      </div>

      {jobsError ? (
        <div className="bg-surface border border-border rounded-lg p-6 text-center mb-8">
          <p className="text-sm text-red-400">{(jobsError as Error).message}</p>
        </div>
      ) : (
        <div className="mb-10">
          <DataTable<PublishJob>
            loading={jobsLoading}
            data={jobs}
            keyExtractor={(j) => j.id}
            columns={[
              {
                key: 'title', label: 'App',
                render: (j) => (
                  <button className="text-accent hover:underline" onClick={() => setOpenJobId(j.id)}>
                    {j.title}
                  </button>
                ),
              },
              { key: 'package_name', label: 'Package', render: (j) => <span className="text-content-muted font-mono text-xs">{j.package_name}</span> },
              { key: 'account_name', label: 'Account', render: (j) => <span className="text-content-muted">{j.account_name}</span> },
              { key: 'template_name', label: 'Template', render: (j) => <span className="text-content-muted">{j.template_name}</span> },
              {
                key: 'status', label: 'Status',
                render: (j) => (
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${JOB_STATUS_CLS[j.status] ?? JOB_STATUS_CLS.pending}`}>
                    {j.status === 'blocked' && j.blocked_reason === 'paused' ? 'stopped' : j.status === 'blocked' && j.blocked_reason ? `blocked · ${j.blocked_reason}` : j.status}
                  </span>
                ),
              },
              {
                key: 'steps', label: 'Steps',
                render: (j) => <span className="text-content-muted">{j.steps_done}/{j.steps_total}</span>,
              },
              { key: 'updated_at', label: 'Updated', render: (j) => <span className="text-content-muted">{fmtUnix(j.updated_at)}</span> },
            ]}
          />
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-content">Console access</h2>
        <Button size="sm" onClick={() => { setAccNew(''); setAccNewError(null); }}>Add account</Button>
      </div>
      <p className="text-sm text-content-muted mb-4">
        The browser half of each Play account. A publication needs a bound profile, stored
        credentials and a session that was seen signed in — without them the job is blocked before
        a browser opens. The service-account key for the API steps is added later, under Play
        Console accounts, once the developer account exists.
      </p>

      <Modal isOpen={accNew !== null} onClose={() => setAccNew(null)} title="Add Play account">
        <label className="block text-sm text-content-muted mb-1">Name</label>
        <input
          className={`${inputCls} mb-3`}
          autoFocus
          value={accNew ?? ''}
          onChange={(e) => setAccNew(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && accNew?.trim()) addAccMut.mutate(accNew.trim()); }}
          placeholder="e.g. Acct 07"
        />
        {accNewError && <p className="text-red-400 text-sm mb-3">{accNewError}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setAccNew(null)}>Cancel</Button>
          <Button loading={addAccMut.isPending} disabled={!accNew?.trim()} onClick={() => addAccMut.mutate(accNew!.trim())}>
            Add and set up access
          </Button>
        </div>
      </Modal>

      {liveError && (
        <div className="mb-3 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-400">{liveError}</div>
      )}
      <div className="mb-10">
        <DataTable<PublishAccount>
          loading={accLoading}
          data={consoleAccounts}
          keyExtractor={(a) => String(a.id)}
          columns={[
            {
              key: 'name', label: 'Account',
              render: (a) => (
                <span className="flex flex-col items-start gap-0.5">
                  <button className="text-accent hover:underline" onClick={() => setAccEditing(a)}>{a.name}</button>
                  {a.browser_profile_id && (
                    <span className="text-xs text-content-muted" title="Somebody else's developer account; we sign in with a browser profile they invited">
                      shared · via {a.browser_profile_name}
                    </span>
                  )}
                  {a.developer_access === 'lost' && <span className="text-xs text-red-400">owner removed our login</span>}
                </span>
              ),
            },
            { key: 'google_email', label: 'Google account', render: (a) => <span className="text-content-muted">{a.google_email ?? '—'}</span> },
            {
              key: 'shardx_profile_id', label: 'Profile',
              render: (a) => <span className="text-content-muted font-mono text-xs">{a.shardx_profile_id ?? '—'}</span>,
            },
            {
              key: 'session_status', label: 'Session',
              render: (a) => (
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${SESSION_CLS[a.session_status] ?? SESSION_CLS.unknown}`}>
                  {a.session_status}
                </span>
              ),
            },
            { key: 'last_keepalive_at', label: 'Last seen', render: (a) => <span className="text-content-muted">{keepaliveAge(a.last_keepalive_at)}</span> },
            {
              key: 'factors', label: '2FA',
              render: (a) => (
                <span className={a.backup_codes_left <= 3 ? 'text-amber-400' : 'text-content-muted'}>
                  {a.has_totp ? `TOTP · ${a.backup_codes_left} codes` : 'not set up'}
                </span>
              ),
            },
            {
              key: 'daily_publish_cap', label: 'Cap/day',
              render: (a) => <span className="text-content-muted">{a.daily_publish_cap === 0 ? 'off' : a.daily_publish_cap}</span>,
            },
            ...(canLive ? [{
              key: 'live', label: 'Browser',
              render: (a: PublishAccount) => {
                const open = liveByAccount.get(a.id);
                if (open) {
                  return (
                    <span className="flex flex-col gap-1 items-start">
                      <span className="text-green-400 text-xs">{open.status} · {open.admin_email}</span>
                      <Button size="sm" variant="secondary" onClick={() => navigate(`/play/live/${open.id}`)}>View</Button>
                    </span>
                  );
                }
                return (
                  <Button size="sm" variant="secondary"
                    loading={openLiveMut.isPending && openLiveMut.variables === a.id}
                    disabled={!a.shardx_profile_id}
                    title={a.shardx_profile_id
                      ? 'Open this account\'s browser profile on the VPS and drive it from here'
                      : 'Save a proxy first: the account has no browser profile yet'}
                    onClick={() => { setLiveError(null); openLiveMut.mutate(a.id); }}>
                    Open
                  </Button>
                );
              },
            }] : []),
            {
              key: 'sa', label: 'Service account',
              render: (a) => {
                const busy = onboardMut.isPending && onboardMut.variables === a.id;
                if (a.onboard_started_at) return <span className="text-content-muted">setting up…</span>;
                if (a.onboard_requested_at) return <span className="text-content-muted">queued for the runner</span>;
                // A key pasted in by hand before the runner existed (no onboarding
                // project): it may only reach the apps it was granted, while a
                // first publication needs account-wide access. Set up mints the
                // runner's own key and replaces it.
                const manual = !!a.sa_client_email && !a.onboard_project_id && !a.browser_profile_id;
                // A shared console's owner invites the service account by hand.
                const ownerInvites = !!a.browser_profile_id && !!a.sa_client_email && !a.onboard_error;
                return (
                  <span className="flex flex-col gap-1">
                    {a.sa_client_email && <span className="text-content-muted font-mono text-xs break-all">{a.sa_client_email}</span>}
                    {manual && !a.onboard_error && <span className="text-amber-400 text-xs">added by hand — Set up gives the runner account-wide access</span>}
                    {a.onboard_error && <span className="text-red-400 text-xs">{a.onboard_error}</span>}
                    {!a.sa_client_email && !a.onboard_error && <span className="text-content-muted">none</span>}
                    {ownerInvites && (a.sa_invited_at ? (
                      <span className="text-xs text-green-400">
                        invited by the owner ·{' '}
                        <button className="text-content-muted hover:underline" onClick={() => invitedMut.mutate({ id: a.id, invited: false })}>undo</button>
                      </span>
                    ) : (
                      <span className="flex flex-col gap-1 items-start">
                        <span className="text-amber-400 text-xs">
                          The console’s owner invites this address under Users and permissions (all apps, every permission but Admin, users and finance)
                        </span>
                        <span className="flex gap-2">
                          <Button size="sm" variant="secondary" onClick={() => navigator.clipboard.writeText(a.sa_client_email!).catch(() => {})}>Copy</Button>
                          <Button size="sm" variant="secondary" loading={invitedMut.isPending && invitedMut.variables?.id === a.id}
                            onClick={() => { setLiveError(null); invitedMut.mutate({ id: a.id, invited: true }); }}>
                            Owner invited it
                          </Button>
                        </span>
                      </span>
                    ))}
                    {(!a.sa_client_email || manual || a.onboard_error) && (
                      <Button size="sm" variant="secondary" loading={busy} disabled={!a.shardx_profile_id}
                        title={a.shardx_profile_id ? undefined : 'Save a proxy first: the runner needs the account\'s browser profile'}
                        onClick={() => onboardMut.mutate(a.id)}>
                        {a.onboard_error ? 'Retry' : 'Set up'}
                      </Button>
                    )}
                  </span>
                );
              },
            },
          ]}
        />
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-content">Templates</h2>
        <Button variant="secondary" size="sm" onClick={() => { setTplEditing(null); setTplError(null); setTplOpen(true); }}>
          New template
        </Button>
      </div>
      <p className="text-sm text-content-muted mb-4">
        A template holds the questionnaire answers for one app archetype. Authoring them once is
        what makes the answers consistent across every account.
      </p>

      {tplError && (
        <div className="mb-3 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-400">
          {tplError}
        </div>
      )}

      <DataTable<PublishTemplate>
        loading={tplLoading}
        data={templates}
        keyExtractor={(t) => String(t.id)}
        columns={[
          {
            key: 'name', label: 'Name',
            render: (t) => (
              <span className="text-content">
                {t.name}
                {t.for_handoff && (
                  <span
                    className="ml-2 rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary"
                    title="Handoff links of apps never queued for publishing use this template"
                  >
                    share links
                  </span>
                )}
              </span>
            ),
          },
          { key: 'track', label: 'Track', render: (t) => <span className="text-content-muted">{t.track}</span> },
          {
            key: 'summary', label: 'Answers',
            render: (t) => <span className="text-content-muted">{describeTemplate(t)}</span>,
          },
          { key: 'updated_at', label: 'Updated', render: (t) => <span className="text-content-muted">{fmtUnix(t.updated_at)}</span> },
          {
            key: 'actions', label: '',
            render: (t) => (
              <span className="flex justify-end gap-2">
                <Button size="sm" variant="secondary" onClick={() => { setTplEditing(t); setTplError(null); setTplOpen(true); }}>Edit</Button>
                <Button size="sm" variant="secondary" onClick={() => { setTplError(null); deleteTplMut.mutate(t.id); }}>Delete</Button>
              </span>
            ),
          },
        ]}
      />

      <PublishJobDrawer jobId={openJobId} onClose={() => setOpenJobId(null)} />
      <PublishTemplateEditor isOpen={tplOpen} onClose={() => setTplOpen(false)} template={tplEditing} />
      <PublishAccountEditor account={accEditing} onClose={() => setAccEditing(null)} />

      <Modal isOpen={newOpen} onClose={() => setNewOpen(false)} title="New publication" size="2xl">
        {formError && (
          <div className="mb-3 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-400">
            {formError}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-sm text-content-muted mb-1">Play account</label>
            <select className={inputCls} value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })}>
              <option value="">Select…</option>
              {accounts.filter((a) => a.enabled).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm text-content-muted mb-1">Template</label>
            <select className={inputCls} value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })}>
              <option value="">Select…</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-sm text-content-muted mb-1">Package name</label>
            <input className={inputCls} value={form.package_name} onChange={(e) => setForm({ ...form, package_name: e.target.value })} placeholder="com.example.app" />
          </div>
          <div>
            <label className="block text-sm text-content-muted mb-1">Default language</label>
            <input className={inputCls} value={form.default_lang} onChange={(e) => setForm({ ...form, default_lang: e.target.value })} placeholder="en-US" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-sm text-content-muted mb-1">App</label>
            <select
              className={inputCls}
              value={form.app_id}
              onChange={(e) => {
                const app = apps.find((a) => a.id === e.target.value);
                setForm({
                  ...form, app_id: e.target.value, app_build_id: '',
                  // The package is the app's own; typing a different one here is
                  // how a build lands under the wrong listing.
                  package_name: app?.bundle_id || form.package_name,
                  // Texts follow from the app's store listing once it loads.
                  title: '', short_desc: '', full_desc: '',
                });
              }}
            >
              <option value="">Not linked to an app</option>
              {apps.filter((a) => a.platform === 'android').map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            {form.app_id && (
              <div className="mt-2">
                <label className="block text-xs text-content-muted mb-1">Base repository (what the factory builds)</label>
                <select
                  className={inputCls}
                  value={buildConfigQuery.data?.config.base_repo_id ?? ''}
                  disabled={repoMut.isPending || buildConfigQuery.isLoading}
                  onChange={(e) => { if (e.target.value) repoMut.mutate({ appId: form.app_id, repoId: Number(e.target.value) }); }}
                >
                  <option value="">— not set —</option>
                  {(reposQuery.data ?? []).map((r) => (
                    <option key={r.id} value={r.id}>{r.name} · {r.owner}/{r.repo}@{r.branch}</option>
                  ))}
                </select>
                {readiness && !readiness.ok && (
                  <p className="text-xs text-amber-400 mt-1">Not ready to build: {readiness.missing.join(', ')}</p>
                )}
                {readiness?.ok && <p className="text-xs text-content-muted mt-1">Ready to build: repo, keystore and Firebase are in place.</p>}
                <button
                  type="button"
                  className="mt-1 text-xs text-sky-400 hover:underline"
                  onClick={() => setBuildOpen(true)}
                >
                  Build settings and history for this app…
                </button>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm text-content-muted mb-1">AAB for the first release</label>
            <select
              className={inputCls}
              value={form.build_mode}
              onChange={(e) => setForm({ ...form, build_mode: e.target.value as typeof form.build_mode, app_build_id: '' })}
            >
              <option value="new">Build a new one now</option>
              <option value="existing">Use a finished build</option>
              <option value="none">Attach later</option>
            </select>
            <p className="text-xs text-content-muted mt-1">
              {!form.app_id
                ? 'Pick an app first — the factory builds from its repo and signing config.'
                : form.build_mode === 'new'
                  ? 'The factory starts as soon as the job is created; the upload step waits for it.'
                  : form.build_mode === 'existing'
                    ? 'Only builds that finished with an artifact can be uploaded.'
                    : 'The upload step will fail until a build is attached to the job.'}
            </p>
          </div>
        </div>

        {form.build_mode === 'existing' && form.app_id && (
          <div className="mb-3">
            <label className="block text-sm text-content-muted mb-1">Build</label>
            <select className={inputCls} value={form.app_build_id} onChange={(e) => setForm({ ...form, app_build_id: e.target.value })}>
              <option value="">Select…</option>
              {appBuilds
                .filter((b) => b.build_type === 'aab' && b.artifact_r2_key)
                .map((b) => (
                  <option key={b.id} value={b.id}>
                    v{b.version_name} ({b.version_code}) · {fmtUnix(b.created_at)}
                  </option>
                ))}
            </select>
          </div>
        )}

        {form.app_id && (
          <div className="mb-4 rounded-md border border-border p-3">
            <p className="text-sm text-content mb-2">Listing from the app</p>
            <div className="flex items-start gap-3">
              {selectedApp?.icon_url
                ? <img src={selectedApp.icon_url} alt="" className="w-12 h-12 rounded-lg flex-none" />
                : <span className="w-12 h-12 rounded-lg bg-surface-muted flex-none" />}
              <div className="flex-1 min-w-0 text-xs text-content-muted space-y-1">
                <p>Icon: {selectedApp?.icon_url ? 'yes' : <span className="text-amber-400">missing</span>}
                  {' · '}Feature graphic: {featureAsset ? 'yes' : <span className="text-amber-400">missing</span>}
                  {' · '}Screenshots ({form.default_lang}): {defaultShots.length < 2 ? <span className="text-amber-400">{defaultShots.length} (Play needs 2)</span> : defaultShots.length}</p>
                <p>Locales: {(listingsQuery.data ?? []).map((l) => `${l.lang}${l.fits ? '' : ' (over limits, skipped)'}${shotsByLang[l.lang]?.length ? ` · ${shotsByLang[l.lang]!.length} shots` : ''}`).join(', ') || '—'}</p>
                {defaultShots.length > 0 && (
                  <div className="flex gap-1 overflow-x-auto pt-1">
                    {defaultShots.slice(0, 8).map((a) => (
                      <img key={a.id} src={storeAssetUrl(a.media_key)} alt="" className="h-24 rounded border border-border flex-none" />
                    ))}
                    {featureAsset && <img src={storeAssetUrl(featureAsset.media_key)} alt="" className="h-24 rounded border border-border flex-none" />}
                  </div>
                )}
                {assetsQuery.data && (!featureAsset || defaultShots.length < 2) && (
                  <p className="text-amber-400">The listing step will block until the app has a feature graphic and two screenshots (Apps Setup → Share: feature graphic and screenshots per locale).</p>
                )}
              </div>
            </div>
          </div>
        )}
        <label className="block text-sm text-content-muted mb-1">App title</label>
        <input className={`${inputCls} mb-3`} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={30} />

        <label className="block text-sm text-content-muted mb-1">Short description</label>
        <input className={`${inputCls} mb-3`} value={form.short_desc} onChange={(e) => setForm({ ...form, short_desc: e.target.value })} maxLength={80} />

        <label className="block text-sm text-content-muted mb-1">Full description</label>
        <textarea
          className="w-full px-3 py-2 rounded-md bg-background border border-border text-content text-sm h-32 mb-1"
          value={form.full_desc}
          onChange={(e) => setForm({ ...form, full_desc: e.target.value })}
          maxLength={4000}
        />
        <p className="text-xs text-content-muted mb-4">
          Written to the default language. The other locales, icon, feature graphic and
          screenshots come from the selected app (Apps Setup → Store); the listing step blocks
          if the app has no feature graphic or fewer than two screenshots.
        </p>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setNewOpen(false)}>Cancel</Button>
          <Button
            loading={createMut.isPending}
            disabled={
              !form.account_id || !form.template_id || !form.package_name.trim() || !form.title.trim()
              || (form.build_mode === 'new' && !form.app_id)
              || buildBlocked
              || (form.build_mode === 'existing' && !form.app_build_id)
            }
            onClick={() => { setFormError(null); createMut.mutate(); }}
          >
            Create
          </Button>
        </div>
      </Modal>
      <Modal isOpen={buildOpen && !!form.app_id} onClose={() => setBuildOpen(false)} title="Build" size="5xl">
        <p className="text-sm text-content-muted mb-4">
          The same panel as Apps Setup → Build: base repository, signing, version, and the builds
          already made. A build started here shows up under “Use a finished build” once it lands.
        </p>
        {form.app_id && (
          <AppBuildPanel
            key={form.app_id}
            appId={form.app_id}
            onOpenShare={() => { setBuildOpen(false); setNewOpen(false); navigate('/apps-setup'); }}
          />
        )}
      </Modal>

    </div>
  );
}
